import React from 'react';
import { Plus, Users, TrendingUp, Star, Activity, ArrowLeft, ContactRound, X, Tag } from 'lucide-react';
import PageHeader, { PrimaryButton, SecondaryButton } from 'components/ui/PageHeader';
import { Toolbar } from 'components/ui/toolbar';
import { SearchInput } from 'components/ui/search-input';

/* ══════════════════════════════════════════════════════════════════════════
   Leads page header.

   Was a PageHeader plus ~90 lines of hand-built search-bar markup — its own
   input styling, its own clear button, its own "N found" badge — and a
   separately hand-built segment context pill. Both are now composed from the
   shared toolbar parts, so the control heights, focus rings and dark-mode
   treatment match Campaigns, Audiences and Templates exactly.

   Props and callbacks are unchanged, including the `onSearchChange` event
   shape that the parent view expects.
   ══════════════════════════════════════════════════════════════════════════ */

const LeadsHeader = ({
    totalLeads = 0,
    hotLeads = 0,
    highScoreLeads = 0,
    recentActivityLeads = 0,
    shownCount = 0,
    onAddLead,
    isLoading = false,
    LeadsImportComponent = null,
    selectedSegmentName = null,
    onReturnToAudiences,
    searchTerm = '',
    onSearchChange,
}) => {
    const kpis = [
        { icon: Users, value: totalLeads, label: 'Total Leads', tone: 'brand' as const },
        { icon: TrendingUp, value: hotLeads, label: 'Hot Leads', tone: 'warning' as const },
        { icon: Star, value: highScoreLeads, label: 'High Score 80+', tone: 'success' as const },
        { icon: Activity, value: recentActivityLeads, label: 'Recent Activity', tone: 'violet' as const },
    ];

    /* The parent handler is written against a DOM change event, so the shared
       input's plain-string callback is adapted rather than changing the view. */
    const emitSearch = (value: string) =>
        onSearchChange?.({ target: { value } } as React.ChangeEvent<HTMLInputElement>);

    return (
        <>
            <PageHeader
                icon={<ContactRound className="w-5 h-5" />}
                tone="brand"
                title="Leads & Contacts"
                subtitle={
                    !isLoading && totalLeads > 0
                        ? `${totalLeads} total leads · ${shownCount} shown`
                        : 'Manage and track your leads and contacts'
                }
                actions={
                    <>
                        {selectedSegmentName && onReturnToAudiences && (
                            <SecondaryButton
                                icon={<ArrowLeft className="w-4 h-4" />}
                                onClick={onReturnToAudiences}
                            >
                                Audiences
                            </SecondaryButton>
                        )}
                        {LeadsImportComponent}
                        <PrimaryButton icon={<Plus className="w-4 h-4" />} onClick={onAddLead}>
                            Add Lead
                        </PrimaryButton>
                    </>
                }
                kpis={kpis}
                isLoading={isLoading}
            />

            {/* ── Segment context ──
                When the list is scoped to a segment, that scope is the single
                most important thing on the page: it explains why the count is
                lower than the KPI above it. It stays pinned above the toolbar
                with a one-click exit. */}
            {selectedSegmentName && (
                <div
                    role="status"
                    className="flex items-center gap-2.5 px-3.5 py-2.5 mb-3 rounded-xl bg-brand-subtle border border-brand-border"
                >
                    <Tag className="w-3.5 h-3.5 text-brand flex-shrink-0" aria-hidden="true" />
                    <span className="text-meta font-semibold text-brand-on-subtle truncate flex-1 min-w-0">
                        Showing segment <span className="font-bold">{selectedSegmentName}</span>
                    </span>
                    {onReturnToAudiences && (
                        <button
                            type="button"
                            onClick={onReturnToAudiences}
                            className="inline-flex items-center gap-1 text-micro font-semibold text-brand hover:text-brand-hover whitespace-nowrap flex-shrink-0 px-2 py-1 rounded-lg hover:bg-brand-subtle-hover transition-colors focus-ring"
                        >
                            <X className="w-3 h-3" aria-hidden="true" />
                            Clear
                        </button>
                    )}
                </div>
            )}

            <Toolbar
                className="mb-5"
                search={
                    <SearchInput
                        value={searchTerm}
                        onChange={emitSearch}
                        placeholder="Search leads by name, email or phone…"
                        aria-label="Search leads"
                    />
                }
                summary={searchTerm && !isLoading ? `${shownCount} found` : null}
            />
        </>
    );
};

export default LeadsHeader;
